import React, {useMemo, useState} from 'react';
import PropTypes from 'prop-types';

import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import StepContent from '@mui/material/StepContent';
import Button from '@mui/material/Button';
import DialogPanel from '../../../../DialogPanel';
import EditableData from '../EditableData';
import {inventory} from '../../../../proptypes/inventory';
import {getCurrentYear} from '../../../../../utils/getCurrentYear';
import {useTranslation} from 'react-i18next';

const InventoriesDialog = ({
  minYear,
  maxYear,
  inventories,
  isInventoryDialogOpen,
  plantingYear,
  onDialogClose,
  onInventoryAdd
}) => {

  if(!isInventoryDialogOpen){
    return <></>;
  }

  const {t} = useTranslation();

  const sortedInventories = inventories.length && inventories.sort((a, b) => a.year - b.year);

  const STEPS_IDS = {
    YEAR: 'year',
    PLANTING_YEAR: 'plantingYear',
    AGE: 'age',
    NUMBER_PER_HA: 'number_per_ha',
    HEIGHT: 'dominant_height',
    BASIMETRIC_AREA: 'basimetric_area'
  };

  const initialInventory = {
    year: Math.min(maxYear, getCurrentYear()),
    number_per_ha: (sortedInventories && sortedInventories[sortedInventories.length-1][STEPS_IDS.NUMBER_PER_HA]) || 1000,
    dominant_height: (sortedInventories && sortedInventories[sortedInventories.length-1][STEPS_IDS.HEIGHT]) || 12,
    basimetric_area: (sortedInventories && sortedInventories[sortedInventories.length-1][STEPS_IDS.BASIMETRIC_AREA]) || 100,
  };

  const [activeStep, setActiveStep] = useState(0);
  const [newInventory, setNewInventory] = useState(initialInventory);

  const handleBack = () => setActiveStep(activeStep - 1);
  const handleNext = () => setActiveStep(activeStep + 1);

  const handleAddInventory = () => {
    onInventoryAdd({
      number_per_ha: newInventory.number_per_ha,
      dominant_height: newInventory.dominant_height,
      basimetric_area: newInventory.basimetric_area,
      age: newInventory.year - plantingYear + 1,
      year: newInventory.year
    });
    onDialogClose();
    setActiveStep(0);
  };

  const handlePropertyChange = (property) => {
    setNewInventory({
      ...newInventory,
      ...property
    });
  };

  const steps = [
    {
      id: STEPS_IDS.YEAR,
      sublabel: t('Año del Inventario'),
      sublabelWithSelectedOption: `${('Año')}: ${newInventory.year}`,
      content: <Box my={1}>
        <EditableData
          onChange={(value) => handlePropertyChange({year: value})}
          value={newInventory.year}
          min={minYear}
          max={Math.min(maxYear, getCurrentYear())}
        />
        {minYear ? <Typography variant='body2' component='span' sx={{mt: 1, color: 'grey'}}>{`Min ${minYear} - `}</Typography> : <></>}
        <Typography variant='body2' component='span' sx={{mt: 1, color: 'grey'}}>{`Máx ${Math.min(maxYear, getCurrentYear())}`}</Typography>
      </Box>
    },
    {
      id: STEPS_IDS.NUMBER_PER_HA,
      sublabel: t('Nª de árboles por hectárea (N)'),
      sublabelWithSelectedOption: `${t('N')}: ${newInventory.number_per_ha}`,
      content: <Box my={1}>
        <EditableData
          onChange={(value) => handlePropertyChange({number_per_ha: value})}
          value={newInventory.number_per_ha}
          min={0}
        />
      </Box>
    },
    {
      id: STEPS_IDS.HEIGHT,
      sublabel: t('Altura dominante (Ho)'),
      sublabelWithSelectedOption: `${t('Ho')}: ${newInventory.dominant_height}`,
      content: <Box my={1}>
        <EditableData
          onChange={(value) => handlePropertyChange({dominant_height: value})}
          value={newInventory.dominant_height}
          min={0}
        />
      </Box>
    },
    {
      id: STEPS_IDS.BASIMETRIC_AREA,
      sublabel: t('Área basimétrica (G)'),
      sublabelWithSelectedOption: `${t('G')}: ${newInventory.basimetric_area}`,
      content: <Box my={1}>
        <EditableData
          onChange={(value) => handlePropertyChange({basimetric_area: value})}
          value={newInventory.basimetric_area}
          min={0}
        />
      </Box>
    }
  ];

  const calculatedSteps = useMemo(() => 
    inventories.length
      ? [...steps.filter(step => step.id !== STEPS_IDS.PLANTING_YEAR)]
      : [...steps], [steps]);

  return <DialogPanel
    isOpen={isInventoryDialogOpen}
    onClose={onDialogClose}
    label={t('Añadir Inventario').toUpperCase()}
    fullWidth
  >
    <Stepper activeStep={activeStep} orientation="vertical" sx={{ml: 2, mb: 2}}>
      {
        calculatedSteps.map(({id, label, sublabel, sublabelWithSelectedOption, content}) =>
          <Step key={id}>
            <StepLabel>{activeStep + 1 > id ? sublabelWithSelectedOption : sublabel}</StepLabel>
            <StepContent>
              <Typography>{label}</Typography>
              {content}
              <Box mt={2}>
                <Button disabled={activeStep === 0} onClick={handleBack}>{t('atrás')}</Button>
                <Button variant='contained' color='secondary'
                  onClick={
                    activeStep === calculatedSteps.length - 1
                      ? handleAddInventory
                      : handleNext
                  }
                >
                  {
                    activeStep === calculatedSteps.length - 1
                      ? t('guardar').toUpperCase()
                      : t('siguiente').toUpperCase()
                  }
                </Button>
              </Box>
            </StepContent>
          </Step>
        )
      }
    </Stepper>
  </DialogPanel>;
};

InventoriesDialog.propTypes = {
  minYear: PropTypes.number.isRequired,
  maxYear: PropTypes.number.isRequired,
  inventories: PropTypes.arrayOf(inventory),
  isInventoryDialogOpen: PropTypes.bool.isRequired,
  plantingYear: PropTypes.number,
  onDialogClose: PropTypes.func.isRequired,
  onInventoryAdd: PropTypes.func.isRequired
};

export default InventoriesDialog;